import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { getApiUrl } from '../config';
import './Budget.css';

const Budget = ({ config }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('thismonth');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [allTransactions, setAllTransactions] = useState([]);
  const [budgetData, setBudgetData] = useState(null);

  useEffect(() => {
    loadBudgetData();
  }, [config, period, selectedMonth]);

  // Load all transactions once for month dropdown
  useEffect(() => {
    const loadAllTransactions = async () => {
      try {
        const response = await axios.post(getApiUrl('/api/hledger/command'), {
          journalPath: config.journalPath,
          command: 'print --output-format=json'
        });
        const data = JSON.parse(response.data.stdout || '[]');
        setAllTransactions(data);
      } catch (err) {
        console.error('Error loading all transactions for dropdown:', err);
      }
    };

    if (config?.journalPath) {
      loadAllTransactions();
    }
  }, [config]);

  const loadBudgetData = async () => {
    setLoading(true);
    setError('');

    try {
      // Use selectedMonth if set, otherwise use period
      const budgetPeriod = selectedMonth || period;

      const response = await axios.post(getApiUrl('/api/hledger/budget'), {
        journalPath: config.journalPath,
        period: budgetPeriod
      });

      console.log('Budget API response:', response.data);
      console.log('Budget data structure:', JSON.stringify(response.data.data, null, 2));

      setBudgetData(response.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Budget error:', err);
      setError('Error loading budget data: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    let year, month;
    if (selectedMonth) {
      const parts = selectedMonth.split('-');
      year = parseInt(parts[0]);
      month = parseInt(parts[1]) - 1; // Convert to 0-indexed
    } else {
      const now = new Date();
      year = now.getFullYear();
      month = now.getMonth();
    }

    // Create date using explicit parameters to avoid timezone issues
    const currentDate = new Date(year, month, 1);
    currentDate.setMonth(currentDate.getMonth() + direction);

    const newYear = currentDate.getFullYear();
    const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0'); // Convert back to 1-indexed
    setSelectedMonth(`${newYear}-${newMonth}`);
    setPeriod(''); // Clear period when using month navigation
  };

  const parseBudgetData = () => {
    if (!budgetData) {
      console.log('No budget data');
      return [];
    }

    console.log('Parsing budget data, type:', typeof budgetData, 'isArray:', Array.isArray(budgetData));

    // hledger balance --budget returns periodic report format: {prDates, prRows, prTotals}
    if (!budgetData.prRows || !Array.isArray(budgetData.prRows)) {
      console.log('Budget data has no prRows or prRows is not array');
      return [];
    }

    const items = [];

    // Each row has: prrName, prrTotal: [[actual], [budget]]
    budgetData.prRows.forEach((row, idx) => {
      console.log(`Row ${idx}:`, row);

      const accountName = row.prrName;

      // Skip <unbudgeted> and parent accounts (Assets, Expenses)
      if (accountName === '<unbudgeted>' || !accountName.includes(':')) {
        console.log(`  - Skipping ${accountName}`);
        return;
      }

      console.log(`  - accountName: ${accountName}`);
      console.log(`  - prrTotal:`, row.prrTotal);

      let actual = 0;
      let budgeted = 0;

      // prrTotal[0] is array of actual amounts
      // prrTotal[1] is array of budget amounts
      if (row.prrTotal && Array.isArray(row.prrTotal)) {
        if (row.prrTotal[0] && Array.isArray(row.prrTotal[0]) && row.prrTotal[0].length > 0) {
          actual = Math.abs(row.prrTotal[0][0]?.aquantity?.floatingPoint || 0);
        }

        if (row.prrTotal[1] && Array.isArray(row.prrTotal[1]) && row.prrTotal[1].length > 0) {
          budgeted = Math.abs(row.prrTotal[1][0]?.aquantity?.floatingPoint || 0);
        }
      }

      console.log(`  - actual: ${actual}, budgeted: ${budgeted}`);

      // Only include accounts that have a budget set
      if (budgeted > 0) {
        items.push({
          account: accountName.split(':').pop(), // Get last part of account name
          fullAccount: accountName,
          actual: actual,
          budgeted: budgeted
        });
      }
    });

    console.log('Parsed budget items:', items);
    return items;
  };

  const getProgressColor = (percentage) => {
    if (percentage > 100) return 'danger';
    if (percentage > 80) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading budget data...</p>
      </div>
    );
  }

  const budgetItems = parseBudgetData();

  return (
    <div className="budget-page">
      <div className="page-header">
        <h1>Budget Tracking</h1>
        <p>Monitor spending against budgeted amounts</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Period</h2>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: '0 0 200px' }}>
              <label className="form-label">Quick Period</label>
              <select
                className="form-select"
                value={period}
                onChange={(e) => {
                  setPeriod(e.target.value);
                  if (e.target.value) setSelectedMonth(''); // Clear month when selecting period
                }}
              >
                <option value="">Select period...</option>
                <option value="thismonth">This Month</option>
                <option value="lastmonth">Last Month</option>
                <option value="thisquarter">This Quarter</option>
                <option value="thisyear">This Year</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: '1 1 400px' }}>
              <label className="form-label">Or Select Specific Month</label>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button
                  className="btn btn-sm"
                  onClick={() => navigateMonth(-1)}
                  title="Previous month"
                  style={{ padding: '0.25rem 0.5rem' }}
                >
                  ◀
                </button>
                <select
                  className="form-select"
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setPeriod(''); // Clear period when selecting month
                  }}
                  style={{ flex: 1 }}
                >
                  <option value="">Select month...</option>
                  {(() => {
                    const months = [];
                    const now = new Date();

                    // Find earliest and latest transaction dates from ALL transactions
                    let minDate = now;
                    let maxDate = now;

                    if (allTransactions.length > 0) {
                      allTransactions.forEach(tx => {
                        if (tx.tdate) {
                          const txDate = new Date(tx.tdate);
                          if (txDate < minDate) minDate = txDate;
                          if (txDate > maxDate) maxDate = txDate;
                        }
                      });
                    }

                    // Use current month as max if it's later than latest transaction
                    if (now > maxDate) maxDate = now;

                    // Generate options from latest month down to earliest transaction month
                    // Newest first
                    const startYear = maxDate.getFullYear();
                    const startMonth = maxDate.getMonth();
                    const endYear = minDate.getFullYear();
                    const endMonth = minDate.getMonth();

                    let currentYear = startYear;
                    let currentMonth = startMonth;

                    while (currentYear > endYear || (currentYear === endYear && currentMonth >= endMonth)) {
                      const year = currentYear;
                      const month = String(currentMonth + 1).padStart(2, '0');
                      const value = `${year}-${month}`;
                      const date = new Date(year, currentMonth, 1);
                      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                      months.push(<option key={value} value={value}>{label}</option>);

                      // Move to previous month
                      currentMonth--;
                      if (currentMonth < 0) {
                        currentMonth = 11;
                        currentYear--;
                      }
                    }

                    return months;
                  })()}
                </select>
                <button
                  className="btn btn-sm"
                  onClick={() => navigateMonth(1)}
                  title="Next month"
                  style={{ padding: '0.25rem 0.5rem' }}
                >
                  ▶
                </button>
                {selectedMonth && (
                  <button
                    className="btn btn-sm"
                    onClick={() => setSelectedMonth('')}
                    title="Clear month filter"
                    style={{ padding: '0.25rem 0.5rem' }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Budget vs Actual</h2>
          <button className="btn btn-sm" onClick={loadBudgetData}>
            Refresh
          </button>
        </div>
        <div className="card-body">
          {budgetItems.length === 0 ? (
            <div className="empty-state">
              <p>No budget data found</p>
              <p className="text-muted" style={{ fontSize: '13px', marginTop: '0.5rem' }}>
                Make sure your journal includes periodic transactions (starting with ~)
              </p>
            </div>
          ) : (
            <div className="budget-list">
              {budgetItems.map((item, index) => {
                const percentage = item.budgeted > 0 ? (item.actual / item.budgeted) * 100 : 0;
                const color = getProgressColor(percentage);

                return (
                  <div key={index} className="budget-item">
                    <div className="budget-item-header">
                      <span className="budget-account">{item.account}</span>
                      <span className={`badge badge-${color}`}>
                        {percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="budget-amounts">
                      <span className="budget-actual">
                        {formatCurrency(item.actual)} spent
                      </span>
                      <span className="budget-total text-muted">
                        of {formatCurrency(item.budgeted)}
                      </span>
                    </div>
                    <div className="progress">
                      <div
                        className={`progress-bar progress-bar-${color}`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      ></div>
                    </div>
                    <div className="budget-remaining">
                      {item.budgeted - item.actual >= 0 ? (
                        <span className="text-success">
                          {formatCurrency(item.budgeted - item.actual)} remaining
                        </span>
                      ) : (
                        <span className="text-danger">
                          {formatCurrency(Math.abs(item.budgeted - item.actual))} over budget
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="alert alert-info">
        <strong>Budget Setup:</strong> Define budgets in your journal using periodic transactions.
        Example: <code>~ monthly from 2024-01</code> followed by expense accounts and amounts.
      </div>
    </div>
  );
};

export default Budget;
